import { ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { setCraApiVariable } from 'redux/actions/CraApiVariableActions';
import MessageUtil from 'util/MessageUtil';
import FormicaInput from 'components/custom/FormicaInput';
import RoleHelper from 'RoleHelper';
import { AnimatedButton } from '@trlogic/ui-pack';
import ComponentName from 'constant/ComponentName';
import LanguageUtil from 'util/LanguageUtil';
import CustomTable from 'components/custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import { customConfirm } from 'components/custom/CustomPopupConfirm';
import { ApiVariableParameter } from 'domain/remote/httpVariable/HttpVariableDomains';
import CustomInput from 'components/custom/CustomInput';
import Icon from 'icons/Icon';
import EmptyListComponent from 'components/custom/empty-components/EmptyListComponent';

interface Parameter {
  key: string;
  value: string;
  description: string;
}

export default function HeadersComponent({ name }: { name: string }) {
  const { craApiVariables } = useSelector((state: RootState) => state.craApiVariable);
  const dispatch = useDispatch();
  const currentVariable = craApiVariables[name];
  const [parameter, setParameter] = useState<Parameter>({
    key: '',
    value: '',
    description: '',
  });

  if (!currentVariable) {
    return null;
  }

  const handleSubmit = () => {
    if (currentVariable.headers[parameter.key]) {
      MessageUtil.warning({
        summary: 'Header with this key already exists',
        detail: <div>{parameter.key} can't added.</div>,
      });
      return false;
    }
    const updated = {
      ...currentVariable,
      headers: {
        ...currentVariable.headers,
        [parameter.key]: { value: parameter.value, description: parameter.description },
      },
    };
    dispatch(setCraApiVariable(name, updated));
    setParameter({ ...parameter, key: '', value: '', description: '' });
  };

  const prepareHeaders = () => {
    const keys = Object.keys(currentVariable.headers) as string[];
    const headers = [];
    for (let i = 0; i < keys.length; i++) {
      headers.push({
        key: keys[i],
        value: currentVariable.headers[keys[i]].value,
        description: currentVariable.headers[keys[i]].description,
      });
    }
    return headers;
  };

  const deleteApiHeader = (header: { key: string; value: string | number[] }) => {
    const updated = { ...currentVariable };
    delete updated.headers[header.key];
    dispatch(setCraApiVariable(name, updated));
  };

  const getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'key',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.headers.table.key', defaultMessage: 'Key' }),
        className: 'w-1/4',
        body: (headerParameter: any) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            value={headerParameter.key}
            data-testid={`${ComponentName.API_VARIABLE}_header_key_input_${headerParameter.key}`}
          />
        ),
      },
      {
        field: 'value',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.headers.table.value', defaultMessage: 'Value' }),
        className: 'w-1/4',
        body: (headerParameter: any) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeValue(e, headerParameter)}
            value={headerParameter.value}
            data-testid={`${ComponentName.API_VARIABLE}_header_value_input_${headerParameter.key}`}
          />
        ),
      },
      {
        field: 'description',
        header: 'Description',
        className: 'w-1/3',
        body: (headerParameter: any) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            value={headerParameter.description}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeDescription(e, headerParameter)}
            data-testid={`${ComponentName.API_VARIABLE}_header_description_input_${headerParameter.key}`}
          />
        ),
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.headers.table.action', defaultMessage: 'action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        alignHeader: 'center',
        sortable: false,
        body: (variableParameter: ApiVariableParameter) => (
          <div
            onClick={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
              RoleHelper.canEdit('EnrichmentApi') &&
                customConfirm({
                  target: e.currentTarget,
                  accept: () => deleteApiHeader(variableParameter),
                  message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variable?' }),
                });
            }}
            data-testid={`${ComponentName.API_VARIABLE}_header_remove_button_${variableParameter.key}`}
          >
            <Icon
              icon={'delete'}
              size={20}
              color={'primary'}
              className={'hover:text-primary text-[#607080]  hover:cursor-pointer flex items-center justify-center'}
            />
          </div>
        ),
      },
    ];
  };

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>, headerParameter: any) => {
    if (!headerParameter.key.includes('Authorization')) {
      const updated = {
        ...currentVariable,
        headers: {
          ...currentVariable.headers,
          [headerParameter.key]: {
            value: e.target.value,
            description: currentVariable.headers[headerParameter.key]?.description || '',
          },
        },
      };
      dispatch(setCraApiVariable(name, updated));
    } else {
      MessageUtil.warning({
        summary: 'Authorization',
        detail: (
          <div>
            You can only change "Authorization" header in <b>Authorization</b> tab
          </div>
        ),
      });
    }
  };

  const onChangeDescription = (e: ChangeEvent<HTMLInputElement>, headerParameter: any) => {
    const updated = {
      ...currentVariable,
      headers: {
        ...currentVariable.headers,
        [headerParameter.key]: {
          value: currentVariable.headers[headerParameter.key]?.value || '',
          description: e.target.value,
        },
      },
    };
    dispatch(setCraApiVariable(name, updated));
  };

  return (
    <div className={'flex flex-col w-full h-full mt-5'}>
      <div>
        <div className={'flex w-full gap-x-4 items-center justify-start mb-5 px-1'}>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, key: e.currentTarget.value })}
              value={parameter.key}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.key', defaultMessage: 'Key' })}
              data-testid={`${ComponentName.API_VARIABLE}_header_creator_key_input`}
            />
          </div>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, value: e.currentTarget.value })}
              value={parameter.value}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.value', defaultMessage: 'Value' })}
              data-testid={`${ComponentName.API_VARIABLE}_header_creator_value_input`}
            />
          </div>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, description: e.currentTarget.value })}
              value={parameter.description}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
              data-testid={`${ComponentName.API_VARIABLE}_header_creator_description_input`}
            />
          </div>
          <div className={''}>
            <AnimatedButton
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onClick={handleSubmit}
              customSize={'sm'}
              icon={'add'}
              customType={'primary'}
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
              data-testid={`${ComponentName.API_VARIABLE}_header_creator_button`}
            />
          </div>
        </div>
      </div>
      <div className='h-full pb-5'>
        {Object.keys(currentVariable.headers).length <= 0 ? (
          <EmptyListComponent />
        ) : (
          <div className={'overflow-y-scroll lg:!max-h-[calc(100vh-17rem)] border-[1px] rounded-lg'}>
            <CustomTable columns={getColumns()} value={prepareHeaders()} />
          </div>
        )}
      </div>
    </div>
  );
}
